import React from 'react';
import { createRoot } from 'react-dom/client';
import '@esri/calcite-components/dist/calcite/calcite.css';

import 'react-toastify/dist/ReactToastify.css';
import 'react-data-grid/lib/styles.css';
import './index.css';

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ToastContainer, toast } from 'react-toastify';

import { BrowserRouter as Router } from 'react-router-dom';
import { setAssetPath } from '@esri/calcite-components/dist/components';
import { StringsProvider } from './data/stringsContext';

import data from './data/strings';

import './calcite-imports';
import { AppWrapper } from './components/Router';
import { AccountManagerProvider } from './contexts/AccountManagerContext';
import { IOAuth2Options } from '@esri/arcgis-rest-request';

// This is to prevent react-data-grid from having weird scroll behavior
HTMLElement.prototype.scrollIntoView = function () {};

setAssetPath(window.location.href);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof Error) {
        toast.error(`${error.message}`);
      }
    },
  }),
});

// Default options for authenticating a user
const defaultOptions: IOAuth2Options = {
  clientId: import.meta.env.VITE_CLIENT_ID ?? '',
  redirectUri: window.location.origin + window.location.pathname,
  popup: true,
  portal: 'https://www.arcgis.com/sharing/rest',
};

const container = document.querySelector('#app');
if (!container) throw new Error('No app container found');
const root = createRoot(container);
root.render(
  <Router basename={import.meta.env.BASE_URL}>
    <StringsProvider value={data}>
      <QueryClientProvider client={queryClient}>
        <AccountManagerProvider defaultOptions={defaultOptions}>
          <AppWrapper />
        </AccountManagerProvider>
        <ToastContainer
          position="bottom-right"
          autoClose={10_000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </QueryClientProvider>
    </StringsProvider>
  </Router>
);
